import React, { useCallback, useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { usePOCreation } from '../contextProvider/POCreationContext';
import * as systemMasterAction from '../../../SystemMasters/Store/SystemMasters.action';

/**
 * ShippingDetailsForPO Component
 * Handles shipping information for Purchase Order creation
 * All controls are enabled for PO creation (product-level override)
 */
export default function ShippingDetailsForPO({ isPreviewMode = false, isEditMode = false }) {
  const { t } = useTranslation('purchase-order');
  const dispatch = useDispatch();
  const { creationState, handlePOFieldChange, poProductDetails, isFieldsDisabledByStatus } = usePOCreation();
  const [increaseZIndex, setIncreaseZIndex] = useState(false);
  const { incotermsList } = useSelector((state) => state.SystemMastersDetails);
  const isSupplierLoggedIn = useSelector((state) => state.UserDetails?.isSupplierLoggedIn);


  const shipmentMethodOptions = [
    { _id: 'air', name: 'Air Freight' },
    { _id: 'sea', name: 'Sea Freight' },
    { _id: 'road', name: 'Road Transport' },
    { _id: 'rail', name: 'Rail Transport' },
  ];

  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target || e;
      const currentShippingDetails = creationState?.shippingDetails || {};

      const updatedShippingDetails = {
        ...currentShippingDetails,
        [name]: value,
      };

      handlePOFieldChange('shippingDetails', updatedShippingDetails);
    },
    [creationState?.shippingDetails, handlePOFieldChange],
  );

  const shippingDetails = creationState?.shippingDetails || {};

  useEffect(() => {
    const fetchIncoterms = async () => {
      try {
        await dispatch(systemMasterAction.getIncotermsDetails({ isActive: true }));
      } catch (error) {
        console.error('Error: ShippingDetailsForPO: fetchIncoterms:-', error);
      }
    };
    if (!incotermsList || incotermsList.length === 0) {
      fetchIncoterms();
    }
  }, [dispatch, incotermsList]);

  const factoryShippingDetails = useMemo(() => {
    if (creationState?.selectedFactory && poProductDetails?.supplierFactories?.length > 0) {
      const factory = poProductDetails?.supplierFactories.find((f) => f._id === creationState?.selectedFactory._id) || creationState?.selectedFactory;
      return {
        portOfLoading: factory?.portOfLanding || '',
      };
    }
    return {};
  }, [poProductDetails, creationState?.selectedFactory]);

  /**
   * Handle shipment method selection change
   * @param {Object} selectedOption - Selected option
   */
  const handleShipmentMethodChange = useCallback(
    (selectedOption) => {
      handleInputChange({
        target: {
          name: 'shipmentMethod',
          value: selectedOption?._id || '',
        },
      });
    },
    [handleInputChange],
  );

  const handleIncotermsChange = useCallback(
    (selectedOption) => {
      handleInputChange({
        target: {
          name: 'incoterms',
          value: selectedOption?._id || null,
        },
      });
    },
    [handleInputChange],
  );

  const incotermsOptions = useMemo(
    () =>
      incotermsList
        ?.filter((item) => item.isActive)
        ?.map((item) => ({
          _id: item._id,
          value: item._id,
          label: item.name,
          name: item.name,
        })) || [],
    [incotermsList],
  );

  const handleMenuOpen = () => {
    setIncreaseZIndex(!increaseZIndex);
  };

  return (
    <div className="inner-container shipping-details-section p-10 mb-8">
      <div className="section-template-title">{t('CREATE_EDIT_PO.SHIPPING_DETAILS')}</div>
      <div className="d-flex">
        <div className={`common-react-select flex-fill mr-16 mb-0 ${increaseZIndex && 'z-index-9'}`}>
          <p className="field-name">{t('CREATE_EDIT_PO.SHIPMENT_METHOD')}</p>
          {isPreviewMode ? (
            <div className="form-control-plaintext">{shipmentMethodOptions.find((opt) => opt._id === shippingDetails.shipmentMethod)?.name || '--'}</div>
          ) : (
            <Select
              options={shipmentMethodOptions}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option._id}
              className="common-select"
              classNamePrefix="common-select"
              menuPosition="fixed"
              placeholder={t('CREATE_EDIT_PO.SELECT_METHOD')}
              value={shipmentMethodOptions.find((opt) => opt._id === shippingDetails.shipmentMethod) || null}
              isClearable
              maxMenuHeight={150}
              onMenuOpen={handleMenuOpen}
              onMenuClose={handleMenuOpen}
              onChange={handleShipmentMethodChange}
              isDisabled={isSupplierLoggedIn || isFieldsDisabledByStatus() || isEditMode}
            />
          )}
        </div>

        <div className="common-react-select flex-fill mr-16 mb-0">
          <p className="field-name">{t('CREATE_EDIT_PO.INCOTERMS')}</p>
          {isPreviewMode ? (
            <div className="form-control-plaintext">{incotermsOptions.find((opt) => opt._id === shippingDetails.incoterms)?.name || '--'}</div>
          ) : (
            <Select
              options={incotermsOptions}
              className="common-select"
              classNamePrefix="common-select"
              placeholder={t('CREATE_EDIT_PO.SELECT_INCOTERMS')}
              value={incotermsOptions.find((opt) => opt._id === shippingDetails.incoterms) || null}
              isClearable
              maxMenuHeight={150}
              onChange={handleIncotermsChange}
              isDisabled={isSupplierLoggedIn || isFieldsDisabledByStatus()}
            />
          )}
        </div>

        <div className="common-input-control flex-fill mr-16 mb-0">
          <p className="field-name">{t('CREATE_EDIT_PO.SHIPPING_COSTS', 'Shipping and Handling Costs')}</p>
          {isPreviewMode ? (
            <div className="form-control-plaintext">{shippingDetails.shippingCosts || '--'}</div>
          ) : (
            <input
              type="number"
              name="shippingCosts"
              placeholder={t('CREATE_EDIT_PO.ENTER_COSTS', 'Enter Amount')}
              className="form-control"
              value={shippingDetails.shippingCosts || ''}
              onChange={handleInputChange}
              min={0}
              disabled={isSupplierLoggedIn || isFieldsDisabledByStatus()}
            />
          )}
        </div>

        <div className="common-input-control flex-fill mr-16 mb-0">
          <p className="field-name">{t('CREATE_EDIT_PO.PORT_OF_LANDING', 'Port of Landing')}</p>
          {isPreviewMode ? (
            <div className="form-control-plaintext">{shippingDetails.portOfLanding || '--'}</div>
          ) : (
            <input
              type="text"
              name="portOfLanding"
              placeholder={t('CREATE_EDIT_PO.ENTER_PORT', 'Enter Port')}
              className="form-control"
              value={shippingDetails.portOfLanding || ''}
              onChange={handleInputChange}
              disabled={isSupplierLoggedIn || isFieldsDisabledByStatus()}
            />
          )}
        </div>

        <div className="common-input-control flex-fill mr-16 mb-0">
          <p className="field-name">{t('CREATE_EDIT_PO.PORT_OF_LOADING', 'Port of Lading')}</p>
          {creationState?.selectedFactory ? (
            <div className="form-control-plaintext">{factoryShippingDetails?.portOfLoading || shippingDetails.portOfLoading || '--'}</div>
          ) : (
            <div className="form-control-plaintext">--</div>
          )}
        </div>
      </div>
    </div>
  );
}

ShippingDetailsForPO.propTypes = {
  isPreviewMode: PropTypes.bool,
  isEditMode: PropTypes.bool,
};

ShippingDetailsForPO.defaultProps = {
  isPreviewMode: false,
  isEditMode: false,
};
